import { useMemo } from 'react';
import {
  assignEmployeeColors,
  chipLabel,
  endMinutesCrossingMidnight,
  extendGanttRange,
  ganttBar,
  ganttBarFromMinutes,
  ganttRange,
  ganttTicks,
  sortCoverageByStart,
  teamsInCoverage,
  timeToMinutes,
  type DayCoverage,
  type Employee,
} from '@lemontaigu/staff';
import { barContentSide, coverageStatusLabel, fmtWindowTime, teamLabel, type PunchInterval } from './logic.js';

// Hour-axis day Gantt (operator UI pass 2026-07-17; reordered 2026-07-19;
// split into Planning + Réalisé 2026-07-22): activities in one CHRONOLOGICAL
// list (by window start), team conveyed by a coloured left bar + coloured
// label font (a legend decodes the colours), one percent-positioned bar per
// activity over a compressed hour scale, bar colour = status, covering
// employees as stable-colour initials chips — the day's shape and gaps at a
// glance (« Planning »). Below it, for today + past days, a second chart
// (« Réalisé ») shows one row per person who punched, sorted by their first
// punch of the day, with a segment per real clock interval — the declared
// plan and the clocked reality read as two distinct charts sharing one hour
// axis, rather than interleaved per-activity sub-rows. Geometry is pure
// @lemontaigu/staff (boardGantt); this file is only the rendering.

/** Team → the CSS class carrying its --team-fg colour (null = the neutral « Autres »). */
const teamClass = (team: string | null): string => `team-${team ?? 'none'}`;

/**
 * Below this share of the axis a bar cannot hold its own time label + chips
 * (2026-08-06): a 1 h activity on a 19 h axis is ~5 %, so « Facturation
 * 21:30–22:30 » rendered as an empty sliver. Narrower than this the bar keeps a
 * minimum width so it stays visible as a colour mark.
 *
 * Since 2026-08-09 the CONTENT is always outside the bar (see `barContentSide`),
 * so this no longer decides placement — only that the sliver stays clickable.
 */
const NARROW_BAR_PCT = 9;

export function DayGantt({
  coverage,
  employees,
  adjustingId,
  onAdjust,
  onChipClick,
  clockedInIds,
  autoDeclaredIds,
  punchIntervalsByEmployee,
  markClockStatus = false,
}: {
  coverage: DayCoverage;
  employees: Employee[];
  /** Highlighted row (reception's ✎ override editing); null elsewhere. */
  adjustingId?: number | null;
  /** When set, each row gets a ✎ button (reception); omit for read-only (bos). */
  onAdjust?: (activityId: number) => void;
  /** When set, initials chips are clickable — opens the employee's declared week. */
  onChipClick?: (employeeId: string) => void;
  /** Employee ids with a clock entry on this day — chips outside it read as « pas encore pointé ». */
  clockedInIds?: Set<string>;
  /** Employee ids whose presence was auto-declared from a punch (source='clock') — flagged ⚡ (0046). */
  autoDeclaredIds?: Set<string>;
  /** Per-employee real clock intervals — one Gantt segment per punch against the planned bar (ADR-0011 §8, multi-segment). */
  punchIntervalsByEmployee?: Map<string, PunchInterval[]>;
  /** Show the clock-status marker (today + past days; meaningless in the future). */
  markClockStatus?: boolean;
}) {
  // « Réalisé » segments in axis minutes, with the midnight rollover resolved
  // ONCE here (2026-08-06): a 18:57 → 00:43 punch is 1137 → 1483, so it draws
  // its full length instead of stopping short at the axis end.
  const punchSegments = useMemo(() => {
    if (!markClockStatus || !punchIntervalsByEmployee) return [];
    return [...punchIntervalsByEmployee.entries()]
      .filter(([, intervals]) => intervals.length > 0)
      .map(
        ([id, intervals]) =>
          [
            id,
            intervals.map((iv) => {
              const startMin = timeToMinutes(iv.start);
              return {
                startMin,
                endMin: iv.end ? endMinutesCrossingMidnight(startMin, timeToMinutes(iv.end)) : null,
                label: `${iv.start}–${iv.end ?? '…'}`,
              };
            }),
          ] as const,
      )
      .sort(([, a], [, b]) => a[0]!.startMin - b[0]!.startMin);
  }, [punchIntervalsByEmployee, markClockStatus]);

  // The axis must cover the punches too — otherwise an after-midnight clock-out
  // is silently clipped to the last planned activity's hour.
  const range = useMemo(() => {
    let r = ganttRange(coverage.activities);
    for (const [, segments] of punchSegments) {
      for (const s of segments) r = extendGanttRange(r, s.startMin, s.endMin ?? s.startMin);
    }
    return r;
  }, [coverage.activities, punchSegments]);
  const ticks = useMemo(() => ganttTicks(range), [range]);
  const rows = useMemo(() => sortCoverageByStart(coverage.activities), [coverage.activities]);
  const legendTeams = useMemo(() => teamsInCoverage(coverage.activities), [coverage.activities]);
  const colors = useMemo(() => assignEmployeeColors(employees.map((e) => e.id)), [employees]);
  const employeesById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);

  // « Réalisé » rows (today + past days only, 2026-07-22): one row per person
  // who punched, sorted by their FIRST punch start of the day — the desk's
  // question here is "who showed up and when", independent of which
  // activity(ies) the punch covers.
  const realizedRows = punchSegments;

  const axisRow = (
    <div className="gantt-row gantt-axis">
      <div className="gantt-label" />
      <div className="gantt-track">
        {ticks.map((t) => (
          <span key={t.leftPct} className="gantt-tick" style={{ left: `${t.leftPct}%` }}>
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
  const gridLines = ticks.map((t) => <span key={t.leftPct} className="gantt-line" style={{ left: `${t.leftPct}%` }} />);

  return (
    <div className="gantt-charts">
      <div className="gantt gantt-planning">
        {legendTeams.length > 1 && (
          <div className="gantt-legend">
            {legendTeams.map((t) => (
              <span key={t ?? 'none'} className={`gantt-legend-item ${teamClass(t)}`}>
                {t ? teamLabel(t) : 'Autres'}
              </span>
            ))}
          </div>
        )}

        {axisRow}

        {rows.map((c) => {
          const bar = ganttBar(c, range);
          const note = c.override?.note ?? c.activity.windowNote ?? undefined;
          // One chip per person covering the activity: assigned first (★,
          // operator-pinned), then derived coverers.
          const people =
            c.status === 'covered'
              ? [
                  ...c.assigned.map((id) => ({ id, assigned: true })),
                  ...c.coveredBy.map((id) => ({ id, assigned: false })),
                ]
              : [];
          const timeLabel = `${fmtWindowTime(c.windowStart)}–${fmtWindowTime(c.windowEnd)}`;
          const barTitle = `${c.activity.label} ${timeLabel} — ${coverageStatusLabel(c.status)}${note ? ` · ${note}` : ''}`;
          // Row layout (operator, 2026-08-09). The TIME SLOT sits inside the
          // bar — it describes the bar. Everything else (people, metric, status)
          // sits BESIDE it, in the row's empty half: after a morning activity,
          // before an afternoon one. Keeping people and metric off the bar is
          // what stopped « 21,5 éq. » being clipped to « 21,5 é » at its edge.
          //
          // The side comes from the activity's START TIME, which is how the
          // operator described it and how the day reads — « Nettoyage » is a
          // morning job whether it runs to 13:12 or to 15:00 — with a room
          // check so a long morning bar ending at the axis edge still flips
          // instead of overflowing (see `barContentSide`).
          //
          // The 2026-08-06 narrow-bar case survives inside this: a bar too
          // short to hold its own time slot hands it to the outside block.
          const narrow = bar.widthPct < NARROW_BAR_PCT;
          const flipOutside =
            barContentSide(c.windowStart, bar.leftPct, bar.widthPct) === 'before';

          // Built as values rather than inline JSX so the two halves of the
          // outside block can be ORDERED by which side of the bar it landed on
          // without duplicating either of them.
          const chips = people.map(({ id, assigned }) => {
            const e = employeesById.get(id);
            const label = e ? chipLabel(e) : '?';
            const pending = markClockStatus && !clockedInIds?.has(id);
            const auto = !assigned && (autoDeclaredIds?.has(id) ?? false);
            const title = e
              ? `${e.firstName} ${e.lastName}${assigned ? ' — assigné·e' : ''}${pending ? ' — pas encore pointé·e' : ''} — voir sa semaine`
              : id;
            const cls = `gantt-chip${assigned ? ' is-assigned' : ''}${auto ? ' is-auto' : ''}${pending ? ' is-pending' : ''}`;
            const inner = (
              <>
                {assigned && <span className="gantt-chip-star">★</span>}
                {auto && (
                  <span className="gantt-chip-auto" title="Déclaré·e d’après le pointage">
                    ⚡
                  </span>
                )}
                {label}
                {pending && (
                  <span className="gantt-chip-pending" aria-label="pas encore pointé">
                    ⏳
                  </span>
                )}
              </>
            );
            return onChipClick ? (
              <button
                key={id}
                type="button"
                className={`${cls} is-clickable`}
                style={{ background: colors.get(id) }}
                title={title}
                onClick={() => onChipClick(id)}
              >
                {inner}
              </button>
            ) : (
              <span key={id} className={cls} style={{ background: colors.get(id) }} title={title}>
                {inner}
              </span>
            );
          });

          // The status word travels WITH the metric, not with the people: it
          // qualifies the demand (« pas de besoins », « dispensé ») rather than
          // who is on it, and « ⚠ à pourvoir » reads as the metric's verdict.
          const statusFlag =
            c.status === 'gap' ? (
              <span className="gantt-flag">⚠ à pourvoir</span>
            ) : c.status === 'waived' ? (
              <span className="gantt-muted">dispensé</span>
            ) : c.status === 'noDemand' ? (
              <span className="gantt-muted">pas de besoins</span>
            ) : null;

          const metricBadge = c.badge ? (
            <span className="gantt-demand" title={c.badge.labelFr}>
              {c.badge.text}
            </span>
          ) : c.demand > 1 ? (
            <span className="gantt-demand">{c.demand}</span>
          ) : null;

          return (
            <div
              key={c.activity.id}
              className={`gantt-row gantt-arow ${teamClass(c.activity.team)}${adjustingId === c.activity.id ? ' is-adjusting' : ''}`}
            >
              <div className="gantt-label">
                <span className="gantt-label-text" title={note}>
                  {c.activity.label}
                  {c.override && (
                    <span className="gantt-adjusted" title="Horaire ajusté ce jour">
                      ✎
                    </span>
                  )}
                </span>
                {onAdjust && (
                  <button
                    type="button"
                    className="gantt-adjust-btn"
                    title="Ajuster ce jour (horaire / dispense)"
                    onClick={() => onAdjust(c.activity.id)}
                  >
                    {adjustingId === c.activity.id ? '×' : '✎'}
                  </button>
                )}
              </div>
              <div className="gantt-track">
                {gridLines}
                <div
                  className={`gantt-bar is-${c.status}${narrow ? ' is-narrow' : ''}`}
                  style={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%` }}
                  title={barTitle}
                >
                  {/* The shift's time slot rides INSIDE the bar (operator,
                      2026-08-09) — it describes the bar, so it belongs on it.
                      A bar too narrow to hold it hands it to the outside block
                      instead: printing a time inside a 5 %-wide sliver is what
                      made « Facturation 21:30–22:30 » unreadable to begin with. */}
                  {!narrow && <span className="gantt-bar-window">{timeLabel}</span>}
                </div>
                <div
                  className={`gantt-bar-content is-${c.status} is-outside${flipOutside ? ' is-before' : ''}`}
                  style={
                    flipOutside
                      ? { right: `${100 - bar.leftPct}%` }
                      : { left: `${bar.leftPct + bar.widthPct}%` }
                  }
                  title={barTitle}
                >
                  {narrow && <span className="gantt-bar-window">{timeLabel}</span>}
                  {/* Order MIRRORS around the bar (operator, 2026-08-09): left of
                      it reads employees → metric, right of it metric → employees.
                      Both put the metric against the bar and the people on the
                      outer edge, so reading outward from the bar finds the same
                      thing in the same place whichever side a row landed on. */}
                  {flipOutside ? (
                    <>
                      {chips}
                      {statusFlag}
                      {metricBadge}
                    </>
                  ) : (
                    <>
                      {metricBadge}
                      {statusFlag}
                      {chips}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {realizedRows.length > 0 && (
        <div className="gantt gantt-realized">
          <div className="gantt-section-title">Réalisé (pointages)</div>
          {axisRow}

          {realizedRows.map(([id, intervals]) => {
            const e = employeesById.get(id);
            const label = e ? `${e.firstName} ${e.lastName.charAt(0)}.` : id;
            const title = e ? `${e.firstName} ${e.lastName} — voir sa semaine` : id;
            return (
              <div key={id} className="gantt-row gantt-erow">
                <div className="gantt-label gantt-elabel">
                  {onChipClick ? (
                    <button type="button" className="gantt-ename is-clickable" title={title} onClick={() => onChipClick(id)}>
                      <span className="gantt-erow-dot" style={{ background: colors.get(id) }} />
                      {label}
                    </button>
                  ) : (
                    <span className="gantt-ename" title={title}>
                      <span className="gantt-erow-dot" style={{ background: colors.get(id) }} />
                      {label}
                    </span>
                  )}
                </div>
                <div className="gantt-track">
                  {gridLines}
                  {intervals.map((iv, i) => {
                    const seg = ganttBarFromMinutes(iv.startMin, iv.endMin ?? range.endMin, range);
                    const segTime = iv.label;
                    return (
                      <div
                        key={i}
                        className={`gantt-ebar is-punch${iv.endMin === null ? ' is-open' : ''}`}
                        style={{ left: `${seg.leftPct}%`, width: `${seg.widthPct}%`, background: colors.get(id) }}
                        title={`${label} ${segTime}`}
                      >
                        <span className="gantt-ebar-time">{segTime}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
